import { Layer, Marker, Source } from "@vis.gl/react-maplibre";
import { FC, useMemo } from "react";

import {
  DRAW_POLYLINE_DASH_ARRAY,
  DRAW_POLYLINE_OPACITY_INACTIVE,
  DRAW_POLYLINE_POINT_RADIUS,
  DRAW_POLYLINE_WEIGHT_ACTIVE,
  MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT,
  MapLibreStationDot,
} from "@ryde/controls";
import {
  DRAWING_POLYLINE_COLOR,
  HALF_FACTOR,
  latLngTuplesToLineStringFeature,
  leafletDashToMapLibreDashArray,
  TLatLngTuple,
} from "@ryde/shared";

import { MIN_LINE_POINTS } from "../route-block-maplibre.constants";

/**
 * The line being drawn right now: a dashed preview through the placed vertices plus a dot on each one, so the
 * user can see (and undo back to) every click. The line needs two points; the dots start from the first.
 */
export const MapLibreCourseDrawingLine: FC<{
  drawingPoints: TLatLngTuple[];
}> = ({ drawingPoints }) => {
  const feature = useMemo(
    () => latLngTuplesToLineStringFeature(drawingPoints),
    [drawingPoints],
  );
  const dotSizePx = DRAW_POLYLINE_POINT_RADIUS * HALF_FACTOR;

  return (
    <>
      {drawingPoints.length >= MIN_LINE_POINTS && (
        <Source id="maplibre-course-drawing" type="geojson" data={feature}>
          <Layer
            id="maplibre-course-drawing-line"
            type="line"
            paint={{
              ["line-color"]: DRAWING_POLYLINE_COLOR,
              ["line-width"]: DRAW_POLYLINE_WEIGHT_ACTIVE,
              ["line-opacity"]: DRAW_POLYLINE_OPACITY_INACTIVE,
              ["line-dasharray"]: leafletDashToMapLibreDashArray(
                DRAW_POLYLINE_DASH_ARRAY,
                DRAW_POLYLINE_WEIGHT_ACTIVE,
              ),
            }}
          />
        </Source>
      )}

      {drawingPoints.map(([lat, lng], index) => (
        <Marker
          key={`draw-point-${index}-${lat}-${lng}`}
          longitude={lng}
          latitude={lat}
          anchor="center"
          {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
        >
          <MapLibreStationDot
            $backgroundColor={DRAWING_POLYLINE_COLOR}
            $sizePx={dotSizePx}
          />
        </Marker>
      ))}
    </>
  );
};
